/**
 * SearchPage Component
 * Full search results page with product grid
 */

import { useEffect, useState } from 'react';
import { Layout } from '~/components/layout/Layout';
import { ProductGrid } from '~/components/product/ProductGrid';
import { searchProducts } from '~/lib/shopify/api';
import { generateSearchMetaTags } from '~/lib/seo/meta-tags';
import type { Product, PageInfo } from '~/lib/shopify/types';

interface SearchPageProps {
  query: string;
}

export function SearchPage({ query }: SearchPageProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!query) {
      setProducts([]);
      setIsLoading(false);
      return;
    }

    async function fetchResults() {
      setIsLoading(true);
      setProducts([]);
      try {
        const data = await searchProducts({ query, first: 24 });
        setProducts(data.products);
        setPageInfo(data.pageInfo);
        setTotalCount(data.totalCount);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchResults();
  }, [query]);

  // Update SEO
  useEffect(() => {
    const meta = generateSearchMetaTags(query, totalCount);
    document.title = meta.title;
  }, [query, totalCount]);

  const handleLoadMore = async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;

    setIsLoading(true);
    try {
      const data = await searchProducts({
        query,
        first: 24,
        after: pageInfo.endCursor,
      });
      setProducts((prev) => [...prev, ...data.products]);
      setPageInfo(data.pageInfo);
    } catch (error) {
      console.error('Failed to load more results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container-wide py-8 lg:py-12">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <a href="/" className="text-accent-silver hover:text-accent-white transition-colors">
                Home
              </a>
            </li>
            <li className="text-accent-silver">/</li>
            <li className="text-accent-white">Search</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="heading-md text-accent-white mb-2">
            {query ? `Results for "${query}"` : 'Search'}
          </h1>
          {!isLoading && query && (
            <p className="text-accent-silver">
              {totalCount} {totalCount === 1 ? 'product' : 'products'} found
            </p>
          )}
        </div>

        {/* No query */}
        {!query && (
          <div className="text-center py-16">
            <p className="text-accent-silver">Enter a search term to find products.</p>
          </div>
        )}

        {/* Results */}
        {query && (
          <>
            <ProductGrid products={products} isLoading={isLoading && products.length === 0} listName="Search Results" />

            {/* No Results */}
            {!isLoading && products.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-surface-coal rounded-2xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-accent-white mb-2">No results found</h3>
                <p className="text-accent-silver mb-6">Try a different search term or browse our collections.</p>
                <a href="/products" className="btn-primary">
                  Browse All Products
                </a>
              </div>
            )}

            {/* Load More */}
            {pageInfo?.hasNextPage && (
              <div className="mt-12 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="btn-secondary px-8 py-4"
                >
                  {isLoading ? 'Loading...' : 'Load More Results'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
